import { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Check,
  Factory,
  Layers3,
  PlayCircle,
  QrCode,
  Scissors,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { api } from "../../api.js";

const modules = [
  [
    Factory,
    "Fabric Control",
    "Inward, roll QR, batches, folding and live stock",
  ],
  [
    Scissors,
    "Cutting Flow",
    "Machine planning, actuals, timeline, waste and efficiency",
  ],
  [
    Layers3,
    "Department Operations",
    "Elastic, accessories, production and approvals",
  ],
  [
    Truck,
    "Delivery",
    "Vendor planning, challans, section history and stock issue",
  ],
  [
    BarChart3,
    "Owner Intelligence",
    "Company, user, subscription, sales and lead analytics",
  ],
  [
    QrCode,
    "Traceability",
    "QR labels, plan history, print layouts and downloadable reports",
  ],
];
const requestBlank = {
  companyName: "",
  contactName: "",
  city: "",
  phone: "",
  email: "",
  planCode: "",
  userCount: 5,
  requirements: "",
  departments: [],
};
export default function PublicDemoPage() {
  const [plans, setPlans] = useState([]),
    [mode, setMode] = useState(""),
    [message, setMessage] = useState(""),
    [request, setRequest] = useState(requestBlank),
    [trial, setTrial] = useState({
      companyName: "",
      name: "",
      email: "",
      password: "",
      phone: "",
      city: "",
      departments: ["FABRIC", "CUTTING"],
    });
  useEffect(() => {
    api("/public/saas/plans")
      .then(setPlans)
      .catch(() => {});
  }, []);
  async function sendRequest(e) {
    e.preventDefault();
    try {
      const r = await api("/public/saas/request", {
        method: "POST",
        body: JSON.stringify({ ...request, source: "DEMO_SITE" }),
      });
      setMessage(r.message);
      setRequest(requestBlank);
    } catch (x) {
      setMessage(x.message);
    }
  }
  async function startTrial(e) {
    e.preventDefault();
    try {
      const r = await api("/public/saas/trial", {
        method: "POST",
        body: JSON.stringify(trial),
      });
      setMessage(r.message);
    } catch (x) {
      setMessage(x.message);
    }
  }
  return (
    <main className="demo-site">
      <nav>
        <a href="/demo">
          <Sparkles /> UG SaaS
        </a>
        <div>
          <a href="#features">Features</a>
          <a href="#plans">Plans</a>
          <a href="/">Login</a>
          <button onClick={() => setMode("trial")}>Try Demo</button>
        </div>
      </nav>
      <header className="demo-hero">
        <div>
          <span>
            <ShieldCheck /> Built for garment production
          </span>
          <h1>
            One bright workspace for your entire <em>garment factory.</em>
          </h1>
          <p>
            Fabric to cutting, elastic, accessories, delivery, machine
            timelines, QR traceability, approvals and management
            intelligence—connected in one SaaS platform.
          </p>
          <div>
            <button onClick={() => setMode("trial")}>
              <PlayCircle /> Start Free Trial
            </button>
            <button className="outline" onClick={() => setMode("buy")}>
              Book Live Demo <ArrowRight />
            </button>
          </div>
          <small>
            No card required · Owner-controlled validity · Upgrade anytime
          </small>
        </div>
        <div className="demo-window">
          <header>
            <i />
            <i />
            <i />
            <b>UG SaaS · Live Factory</b>
          </header>
          <section>
            <aside>
              <Sparkles />
              <span />
              <span />
              <span />
              <span />
              <span />
            </aside>
            <div>
              <div className="demo-kpis">
                <b>
                  47<small>Active Plans</small>
                </b>
                <b>
                  92%<small>Efficiency</small>
                </b>
                <b>
                  18<small>Machines Live</small>
                </b>
              </div>
              <div className="demo-chart">
                {[42, 68, 54, 88, 72, 95, 78].map((x, i) => (
                  <i key={i} style={{ height: `${x}%` }} />
                ))}
              </div>
              <div className="demo-rows">
                <span />
                <span />
                <span />
              </div>
            </div>
          </section>
        </div>
      </header>
      <section id="features" className="demo-section">
        <small>FULL FACTORY FLOW</small>
        <h2>Every department. One source of truth.</h2>
        <div className="demo-module-grid">
          {modules.map(([Icon, title, text], i) => (
            <article key={title}>
              <i className={`tone-${i}`}>
                <Icon />
              </i>
              <h3>{title}</h3>
              <p>{text}</p>
              <a onClick={() => setMode("trial")}>Explore module →</a>
            </article>
          ))}
        </div>
      </section>
      <section id="plans" className="demo-section demo-pricing">
        <small>FLEXIBLE PACKAGES</small>
        <h2>Start small. Scale by company.</h2>
        <div>
          {plans.map((p) => (
            <article key={p._id} className={p.featured ? "featured" : ""}>
              {p.featured && <b>RECOMMENDED</b>}
              <h3>{p.name}</h3>
              <strong>
                {p.price
                  ? `₹${Number(p.price).toLocaleString("en-IN")}`
                  : "Owner will configure"}
              </strong>
              <p>
                {p.validityDays} days · Up to {p.maxUsers} users
              </p>
              <ul>
                <li>
                  <Check /> {p.maxDepartments} departments
                </li>
                {p.modules.slice(0, 6).map((x) => (
                  <li key={x}>
                    <Check /> {x}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  setRequest({ ...request, planCode: p.code });
                  setMode(p.code === "TRIAL" ? "trial" : "buy");
                }}
              >
                {p.code === "TRIAL" ? "Try Demo" : "Request Plan"}
              </button>
            </article>
          ))}
        </div>
      </section>
      <section className="demo-cta">
        <h2>Ready to remove production blind spots?</h2>
        <p>
          Register a controlled trial or send your company requirement to the UG
          SaaS owner.
        </p>
        <button onClick={() => setMode("trial")}>Start Trial</button>
        <button onClick={() => setMode("buy")}>Contact Sales</button>
      </section>
      {mode && (
        <div
          className="demo-modal"
          onMouseDown={(e) => e.target === e.currentTarget && setMode("")}
        >
          <form onSubmit={mode === "trial" ? startTrial : sendRequest}>
            <button type="button" className="close" onClick={() => setMode("")}>
              ×
            </button>
            <small>
              {mode === "trial"
                ? "CONTROLLED TRIAL"
                : "PURCHASE / DEMO REQUEST"}
            </small>
            <h2>
              {mode === "trial"
                ? "Create your demo company"
                : "Tell us what your company needs"}
            </h2>
            {mode === "trial" ? (
              <>
                {[
                  ["companyName", "Company name"],
                  ["name", "Your name"],
                  ["email", "Work email", "email"],
                  ["password", "Strong Password (12+ characters)", "password"],
                  ["phone", "Phone"],
                  ["city", "City"],
                ].map(([k, l, t]) => (
                  <label key={k}>
                    <span>{l}</span>
                    <input
                      required={!["phone", "city"].includes(k)}
                      type={t || "text"}
                      value={trial[k]}
                      onChange={(e) =>
                        setTrial({ ...trial, [k]: e.target.value })
                      }
                    />
                  </label>
                ))}
              </>
            ) : (
              <>
                {[
                  ["companyName", "Company name"],
                  ["contactName", "Contact person"],
                  ["phone", "Phone"],
                  ["email", "Email", "email"],
                  ["city", "City"],
                  ["planCode", "Interested plan"],
                  ["userCount", "Users", "number"],
                ].map(([k, l, t]) => (
                  <label key={k}>
                    <span>{l}</span>
                    <input
                      required={!["city", "planCode"].includes(k)}
                      type={t || "text"}
                      value={request[k]}
                      onChange={(e) =>
                        setRequest({
                          ...request,
                          [k]:
                            t === "number"
                              ? Number(e.target.value)
                              : e.target.value,
                        })
                      }
                    />
                  </label>
                ))}
                <label>
                  <span>Requirements</span>
                  <textarea
                    value={request.requirements}
                    onChange={(e) =>
                      setRequest({ ...request, requirements: e.target.value })
                    }
                  />
                </label>
              </>
            )}
            <button className="submit">
              {mode === "trial" ? "Activate Trial" : "Send Request"}
            </button>
            {message && <p className="demo-message">{message}</p>}
          </form>
        </div>
      )}
      <footer className="demo-footer">
        <b>UG SaaS</b>
        <span>Garment Business Flow Manager</span>
        <a href="/privacy">Privacy</a>
        <a href="/">Customer Login</a>
      </footer>
    </main>
  );
}
